/**
 * 非公開（private）Storage バケット用の署名付きURLヘルパー。
 *
 * 公開バケットの getPublicUrl は「URL を知っている人なら誰でも取得できる」ため、
 * 相談添付・ITサポート添付のような機微なファイルには使わない。
 * ここでは Supabase の createSignedUrl / createSignedUrls で短時間だけ有効な URL を発行する。
 *
 * 同じパスを何度も開くケース（スレッド再描画など）で毎回リクエストしないよう、
 * モジュール内にメモリキャッシュを持つ。期限が近づいたら自動で取り直す。
 */
import { supabase } from '@/lib/supabase';

/** 署名付きURLの有効期限（秒）。1時間。 */
export const SIGNED_URL_EXPIRES = 60 * 60;

/** 期限切れとみなす手前の余裕（秒）。 */
const EXPIRY_MARGIN_SEC = 120;

/** キャッシュ1件分。 */
interface CachedSignedUrl {
  url: string;
  /** 有効期限（ミリ秒エポック） */
  expiresAt: number;
}

const cache = new Map<string, CachedSignedUrl>();

/**
 * キャッシュの世代（epoch）。ログアウトなどでキャッシュを破棄するたびに増える。
 *
 * 発行中の createSignedUrls が「破棄したあと」に返ってきてキャッシュを復活させてしまう
 * レース（＝ログアウト後も有効な署名URLがメモリに残る）を防ぐため、
 * 発行開始時の世代と現在の世代が一致するときだけ書き込む。
 */
let cacheGeneration = 0;

/** キャッシュキー（バケット・パス・ダウンロード可否で一意） */
const cacheKeyOf = (bucket: string, path: string, download: boolean): string =>
  `${bucket}::${download ? 'dl' : 'inline'}::${path}`;

/** キャッシュから有効な URL を取り出す（期限切れは破棄） */
function readCache(bucket: string, path: string, download: boolean): string | null {
  const key = cacheKeyOf(bucket, path, download);
  const hit = cache.get(key);
  if (!hit) return null;
  if (hit.expiresAt - EXPIRY_MARGIN_SEC * 1000 <= Date.now()) {
    cache.delete(key);
    return null;
  }
  return hit.url;
}

/** キャッシュへ保存する */
function writeCache(
  bucket: string,
  path: string,
  download: boolean,
  url: string,
  expiresIn: number,
): void {
  cache.set(cacheKeyOf(bucket, path, download), {
    url,
    expiresAt: Date.now() + expiresIn * 1000,
  });
}

/**
 * 1件の署名付きURLを発行する。
 * download=true を指定すると Content-Disposition: attachment（ダウンロード）になる。
 * 権限がない・対象が存在しない場合は null を返す（例外は投げない）。
 */
export async function createStorageSignedUrl(
  bucket: string,
  path: string,
  download = false,
): Promise<string | null> {
  if (!path) return null;

  const cached = readCache(bucket, path, download);
  if (cached) return cached;

  // 発行開始時点の世代を控える（この間にログアウトで破棄されたら書き込まない）
  const generation = cacheGeneration;

  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, SIGNED_URL_EXPIRES, download ? { download: true } : undefined);

    if (error || !data?.signedUrl) return null;
    if (generation === cacheGeneration) {
      writeCache(bucket, path, download, data.signedUrl, SIGNED_URL_EXPIRES);
    }
    return data.signedUrl;
  } catch {
    return null;
  }
}

/**
 * 複数の署名付きURLをまとめて発行する（1リクエスト）。
 * 戻り値は `保存パス → 署名付きURL` のマップ。取得できなかったパスは含まれない。
 */
export async function resolveStorageSignedUrls(
  bucket: string,
  paths: string[],
  download = false,
): Promise<Record<string, string>> {
  const result: Record<string, string> = {};
  const targets = Array.from(new Set(paths.filter(Boolean)));
  if (targets.length === 0) return result;

  const missing: string[] = [];
  targets.forEach((path) => {
    const cached = readCache(bucket, path, download);
    if (cached) result[path] = cached;
    else missing.push(path);
  });

  if (missing.length === 0) return result;

  // 発行開始時点の世代を控える（この間にログアウトで破棄されたら書き込まない）
  const generation = cacheGeneration;

  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrls(missing, SIGNED_URL_EXPIRES);

    if (error || !Array.isArray(data)) return result;

    data.forEach((item) => {
      // item.path は保存パス、item.signedUrl は署名付きURL（失敗時は null）
      if (!item?.signedUrl || !item?.path) return;
      result[item.path] = item.signedUrl;
      // 破棄済み（世代が変わった）なら結果は返すがキャッシュには残さない
      if (generation === cacheGeneration) {
        writeCache(bucket, item.path, download, item.signedUrl, SIGNED_URL_EXPIRES);
      }
    });
  } catch {
    // 通信失敗などは「URL なし」として扱い、呼び出し側で非活性表示にする
  }

  return result;
}

/**
 * キャッシュしている署名付きURLの有効期限（ミリ秒エポック）を返す。
 * 未キャッシュ・期限切れ間近の場合は null を返す。
 * 表示中のURLを期限前に取り直すスケジューリングに使う。
 */
export function getStorageSignedUrlExpiresAt(
  bucket: string,
  path: string,
  download = false,
): number | null {
  const hit = cache.get(cacheKeyOf(bucket, path, download));
  if (!hit) return null;
  if (hit.expiresAt - EXPIRY_MARGIN_SEC * 1000 <= Date.now()) return null;
  return hit.expiresAt;
}

/**
 * 指定したパスのキャッシュだけ破棄する。
 * 添付を削除したあとに古い署名付きURLが残ると、期限まで開けてしまうため。
 */
export function removeStorageSignedUrlCache(bucket: string, path: string): void {
  cache.delete(cacheKeyOf(bucket, path, false));
  cache.delete(cacheKeyOf(bucket, path, true));
}

/**
 * キャッシュを全破棄する。
 * ログアウト（セッション終了）時に必ず呼ぶ。世代を進めることで、
 * 破棄前に発行開始した署名URLが後から返ってきてもキャッシュへ復活できない。
 */
export function clearSignedUrlCache(): void {
  cache.clear();
  cacheGeneration += 1;
}